import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { completeProfile, resendOtp, verifyOtp } from "../../../Features/Postes/Thunk/authThunk";
import { apiRequest } from "../../../Api/Api";
import otpimg from "../../../Assets/otpimg.png";
import "./VerifyOtp.scss";

export default function VerifyOtp() {
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(30);
  const [avatarUploadProgress, setAvatarUploadProgress] = useState(0);
  const [avatarUploadStatus, setAvatarUploadStatus] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const rawMobile = location.state?.phone || "";
  const intent = location.state?.intent || "login";
  const profileDraft = location.state?.profileDraft || null;

  const mobile = useMemo(() => {
    return String(rawMobile || "")
      .replace(/\D/g, "")
      .slice(-10);
  }, [rawMobile]);

  React.useEffect(() => {
    if (!resendCooldown) return undefined;
    const id = window.setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendCooldown]);

  const showError = (msg) => {
    setErrorMsg(msg);
    setTimeout(() => setErrorMsg(""), 2200);
  };

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }

    const joined = newOtp.join("");
    if (joined.length === 4 && /^\d{4}$/.test(joined)) {
      queueMicrotask(() => runVerify(newOtp));
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 4);
    if (!paste) return;

    const newOtp = paste.split("");
    while (newOtp.length < 4) newOtp.push("");
    setOtp(newOtp);

    if (paste.length === 4 && /^\d{4}$/.test(paste)) {
      queueMicrotask(() => runVerify(newOtp));
    }
  };

  const runVerify = async (digits) => {
    const list = Array.isArray(digits) ? digits : otp;
    const finalOtp = list.join("").trim();

    if (!mobile) {
      showError("Mobile number missing. Please go back & try again.");
      return;
    }

    if (finalOtp.length !== 4) {
      showError("Enter valid OTP");
      return;
    }

    try {
      setLoading(true);

      await dispatch(
        verifyOtp({
          mobile,
          otp: finalOtp,
        }),
      ).unwrap();

      // If patient signup captured profile fields on previous step,
      // complete profile immediately to avoid forcing a separate step.
      if (intent === "signup" && profileDraft?.name && profileDraft?.gender) {
        try {
          setAvatarUploadProgress(0);
          setAvatarUploadStatus("");
          await dispatch(
            completeProfile({
              name: String(profileDraft.name || "").trim(),
              gender: String(profileDraft.gender || ""),
              dob: String(profileDraft.dob || ""),
              bloodGroup: String(profileDraft.bloodGroup || ""),
              image: profileDraft?.imageFile || null,
              onAvatarUploadProgress: setAvatarUploadProgress,
              onAvatarUploadStatus: setAvatarUploadStatus,
            })
          ).unwrap();
        } catch {
          // If this fails, routing below still lands the user in the main app (no forced onboarding screen).
        }
      }

      // Route based on actual server state
      const meRes = await apiRequest({
        method: "GET",
        url: "/app/me",
        params: { _t: Date.now() },
        headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
      });
      const me = meRes?.data || meRes || {};
      const user = me?.user || {};

      const routeAfterLanguage = (() => {
        const selectedSelectionsNow = Array.isArray(user?.selectedSelections)
          ? user.selectedSelections
          : [];
        const selectedSubCategoryIdsNow = Array.isArray(user?.selectedSubCategoryIds)
          ? user.selectedSubCategoryIds
          : [];
        const hasSelectionNow =
          selectedSelectionsNow.length > 0 || selectedSubCategoryIdsNow.length > 0;

        const accountStatusNow = String(
          user?.adminApprovalStatus || "",
        ).toLowerCase();

        if (accountStatusNow !== "approved") {
          return hasSelectionNow ? "/pending-approval" : "/HealBidDashboard";
        }

        return hasSelectionNow ? "/care-price" : "/request/new";
      })();

      const preferredLanguage = String(
        user?.preferredLanguage || localStorage.getItem("lang") || "",
      ).toLowerCase();
      const hasPreferredLanguage = ["en", "hi", "bn"].includes(preferredLanguage);
      if (!hasPreferredLanguage) {
        navigate("/LanguagePage", {
          replace: true,
          state: {
            nextRoute: routeAfterLanguage,
            enforceSelection: true,
            sourceIntent: intent,
          },
        });
        return;
      }

      // const selectedSelections = Array.isArray(user?.selectedSelections) ? user.selectedSelections : [];
      // const selectedSubCategoryIds = Array.isArray(user?.selectedSubCategoryIds) ? user.selectedSubCategoryIds : [];
      // const hasSelection = selectedSelections.length > 0 || selectedSubCategoryIds.length > 0;

      const selectedSelections = Array.isArray(user?.selectedSelections)
        ? user.selectedSelections
        : [];
      const selectedSubCategoryIds = Array.isArray(user?.selectedSubCategoryIds)
        ? user.selectedSubCategoryIds
        : [];
      const hasSelection =
        selectedSelections.length > 0 || selectedSubCategoryIds.length > 0;

      const accountStatus = String(
        user?.adminApprovalStatus || "",
      ).toLowerCase();
      if (accountStatus !== "approved") {
        navigate(hasSelection ? "/pending-approval" : "/HealBidDashboard", {
          replace: true,
        });
        return;
      }

      if (!hasSelection) {
        navigate("/request/new", { replace: true });
        return;
      }

      navigate("/care-price", { replace: true });
    } catch (err) {
      showError(err || "OTP verification failed");
    } finally {
      setAvatarUploadStatus("");
      setLoading(false);
    }
  };

  const handleVerify = () => runVerify(otp);

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      setResending(true);
      await dispatch(resendOtp({ mobile, intent })).unwrap();
      setResendCooldown(30);
      showError("OTP resent successfully");
    } catch (err) {
      showError(err || "Resend OTP failed");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="verify-page">
      {errorMsg && <div className="top-alert">{errorMsg}</div>}

      <div className="container">
        <div className="verify-card">
          <figure className="Otpimg">
            <img src={otpimg} alt="" />
          </figure>

          <div className="enterr">
            <h2>Enter Your OTP</h2>
            <p>Enter the code sent to your mobile.</p>
            <p>{mobile}</p>
          </div>

          <div className="otp-wrapper" onPaste={handlePaste} role="group" aria-label="One-time code">
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                inputMode="numeric"
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className="otp-input"
                disabled={loading}
              />
            ))}
          </div>

          <button
            className="verify-btn"
            onClick={handleVerify}
            disabled={loading}
          >
            {loading ? "Verifying..." : "Verify OTP"}
          </button>

          {loading && intent === "signup" && profileDraft?.imageFile ? (
            <div className="verify-upload-progress">
              <div className="verify-upload-progress-head">
                <span className="verify-upload-progress-label">
                  {avatarUploadStatus || "Preparing secure upload..."}
                </span>
                <span className="verify-upload-progress-value">{avatarUploadProgress}%</span>
              </div>
              <div className="verify-upload-progress-track">
                <span
                  className="verify-upload-progress-fill"
                  style={{ width: `${avatarUploadProgress}%` }}
                />
              </div>
            </div>
          ) : null}

          <div className="did">
            <p className="didP">Didn’t receive the OTP?</p>
            <p
              className="res"
              onClick={handleResend}
              style={{
                cursor: resendCooldown > 0 ? "not-allowed" : "pointer",
                opacity: resendCooldown > 0 ? 0.7 : 1,
              }}
            >
              {resending
                ? "Resending..."
                : resendCooldown > 0
                  ? `Resend code in ${resendCooldown}s`
                  : "Resend code"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
